import React, { useCallback, useState } from 'react';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor, type RecutTarget } from '../MusicVideoEditorContext';
import { PhoneEditor } from '../phone/PhoneEditor';
import { useIsPhoneLayout } from '../phone/useIsPhoneLayout';
import { FullScreenPlayer } from '../preview/FullScreenPlayer';
import { WideEditor } from '../WideEditor';
import { ShowcasePhoneTop } from './ShowcasePhoneTop';
import { ShowcaseTopBar } from './ShowcaseTopBar';
import { ShowcaseUpsellDialog } from './ShowcaseUpsellDialog';
import { useShowcaseFileDrop } from './useShowcaseFileDrop';
import { useShowcaseFirstEdit } from './useShowcaseFirstEdit';

interface ShowcaseEditorFrameProps {
  recutTarget: RecutTarget | null;
  onCloseRecut: () => void;
}

/**
 * The frame of the editor in showcase mode (design c1, c5): the same wide and phone layouts as the
 * account frame, with the showcase top bars and the sign-up card. There is no save state, no make
 * flow, no director note and no leave dialog: nothing is saved.
 */
const ShowcaseEditorFrameComponent: React.FC<ShowcaseEditorFrameProps> = ({ recutTarget, onCloseRecut }) => {
  const { canUndo, showUpsell } = useMusicVideoEditor();
  const isPhone = useIsPhoneLayout();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const openFullScreen = useCallback(() => setIsFullScreen(true), []);
  const closeFullScreen = useCallback(() => setIsFullScreen(false), []);
  useShowcaseFirstEdit(canUndo);
  useShowcaseFileDrop(showUpsell);

  return (
    <>
      {isPhone ? (
        <PhoneEditor top={<ShowcasePhoneTop />} notices={null} />
      ) : (
        <WideEditor
          topBar={<ShowcaseTopBar onWatch={openFullScreen} />}
          notices={null}
          recutTarget={recutTarget}
          onCloseRecut={onCloseRecut}
        />
      )}
      {isFullScreen && <FullScreenPlayer onClose={closeFullScreen} />}
      <ShowcaseUpsellDialog />
    </>
  );
};

export const ShowcaseEditorFrame = TypedMemo(ShowcaseEditorFrameComponent);
